import React, { useEffect, useRef, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { CheckboxGroup } from '../CheckboxGroup';
import {
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

export const StyledAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    border: none;
    justify-content: space-between;
    padding: ${SPACERS.tiny} 0;
    &:before {
      content: none;
    }
    .MuiAccordionSummary-content {
      align-self: center;
      justify-self: center;
    }
    .group-name {
      margin: 0;
    }
  }
  .MuiAccordionSummary-root {
    padding-left: 0;
  }
  .accordion-details {
    flex-direction: column;
    margin-top: ${SPACERS.small};
  }
`;

export interface Indicator {
  id?: number;
  name: string;
  stageId?: number;
}

const Indicator = ({ group, handleChange, searchText, setEmpty }) => {
  const [options, setOptions] = useState(group.indicators);
  useEffect(() => {
    if (searchText.length < 3) {
      setOptions(group.indicators);
      setEmpty(false);
      return;
    }
    setOptions(o => {
      const newState = o.filter(v => v.name.includes(searchText));
      if (newState.length > 0) setEmpty(false);
      return newState;
    });
  }, [searchText, setOptions]);
  if (options.length === 0) return null;
  return (
    <StyledAccordion>
      <AccordionSummary
        expandIcon={<FontAwesomeIcon icon={faAngleDown} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <TrueImpactText className="group-name" variant="heading-subheading">
          {group.name}
        </TrueImpactText>
      </AccordionSummary>
      <AccordionDetails>
        <CheckboxGroup
          hideSecondRow
          value={undefined}
          otherArea={false}
          options={options}
          onChange={selection => handleChange(group.id, selection)}
        />
      </AccordionDetails>
    </StyledAccordion>
  );
};

interface GroupsProps {
  indicators: Indicator[];
  handleChange: (indicator: Indicator) => unknown;
  searchText: string;
  setEmpty: (empty: boolean) => unknown;
}

const Groups: React.FC<GroupsProps> = React.memo(
  ({ indicators, handleChange, searchText, setEmpty }) => {
    setEmpty(true);
    return (
      <div className="container">
        {indicators.map(group => (
          <Indicator
            group={group}
            key={group.id}
            searchText={searchText}
            setEmpty={setEmpty}
            handleChange={handleChange}
          />
        ))}
      </div>
    );
  },
);

interface CenterContainerProps {
  indicators: Indicator[];
  handleChange: (indicator: Indicator) => unknown;
}

export const CenterContainer: React.FC<CenterContainerProps> = props => {
  const ref = useRef<{ value: string }>();
  const [search, setSearch] = useState('');
  const [isEmpty, setEmpty] = useState(true);
  const throttled = useMemo(
    () =>
      debounce(() => {
        if (!ref.current) return;
        setSearch(ref.current.value.toLowerCase());
      }, 1000),
    [],
  );
  useEffect(() => {
    return () => {
      throttled.cancel();
    };
  }, []);
  return (
    <>
      <TextField
        inputRef={ref}
        fullWidth
        // value={search}
        label="Search"
        onChange={throttled}
        variant="outlined"
      />
      <Groups {...props} searchText={search} setEmpty={setEmpty} />
      {isEmpty && (
        <p>
          Your search doesn't match any indicators, try something less specific.
        </p>
      )}
    </>
  );
};
